import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtBRL, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Copy, CheckCircle2, ExternalLink, Loader2, RefreshCw, AlertCircle, Ticket, X } from "lucide-react";

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [needsRegen, setNeedsRegen] = useState(false); // MP said cancelled/expired
  const [couponInput, setCouponInput] = useState("");
  const [couponPreview, setCouponPreview] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const pollRef = useRef(null);
  const paidToastRef = useRef(false);
  const isCard = inv?.payment_method === "credit_card";
  const couponCoversInvoice = Boolean(inv?.coupon_code && Number(inv?.amount_brl) === 0);
  const paidUpgradeInProgress = inv?.is_plan_upgrade
    && inv?.status === "paid"
    && !["applied", "failed"].includes(inv?.upgrade_application_state);
  const invoiceStatus = inv?.status;
  const paymentId = inv?.mp_payment_id;

  const load = useCallback(
    () => api.get(`/invoices/${invoiceId}`).then((r) => setInv(r.data)),
    [invoiceId],
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { paidToastRef.current = false; }, [invoiceId]);
  useEffect(() => {
    setCouponInput("");
    setCouponPreview(null);
  }, [invoiceId]);

  // Polling do status via Mercado Pago
  useEffect(() => {
    if (!invoiceStatus) return;
    if (applyingCoupon) return;
    if (invoiceStatus === "paid" && !paidUpgradeInProgress) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    if (isCard && invoiceStatus !== "pending" && !paidUpgradeInProgress) return;
    if (!isCard && !paymentId && !paidUpgradeInProgress) return; // mock PIX mode

    setPolling(true);
    let active = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}/status`);
        if (!active) return;
        if (data.invoice) setInv(data.invoice);
        const status = data.status || data.invoice?.status;
        if (status === "paid") {
          const upgradePending = data.invoice?.is_plan_upgrade
            && !["applied", "failed"].includes(data.invoice?.upgrade_application_state);
          if (upgradePending) {
            if (!paidToastRef.current) {
              toast.success("Pagamento confirmado. Aplicando o upgrade…");
              paidToastRef.current = true;
            }
          } else {
            clearInterval(pollRef.current);
            setPolling(false);
            if (data.invoice?.is_plan_upgrade && data.invoice?.upgrade_application_state === "failed") {
              toast.error("Pagamento confirmado, mas o upgrade precisa de revisão da equipe.");
            } else if (data.invoice?.is_plan_upgrade) {
              toast.success("Upgrade aplicado com sucesso.");
            } else {
              toast.success("Pagamento confirmado! A entrega da sua VPS foi iniciada.");
            }
          }
        } else if (!isCard && (status === "cancelled" || data.mp_status === "cancelled" || data.mp_status === "expired")) {
          clearInterval(pollRef.current);
          setPolling(false);
          setNeedsRegen(true);
        }
      } catch {/* silent, keep polling */}
    };
    tick();
    pollRef.current = setInterval(tick, 5000);
    return () => {
      active = false;
      if (pollRef.current) clearInterval(pollRef.current);
      setPolling(false);
    };
  }, [invoiceStatus, paymentId, isCard, paidUpgradeInProgress, invoiceId, applyingCoupon]);

  const simulatePay = async () => {
    setPaying(true);
    try {
      await api.post(`/invoices/${invoiceId}/pay`);
      toast.success(inv?.is_plan_upgrade
        ? "Pagamento confirmado. O upgrade será aplicado."
        : "Pagamento confirmado! Sua VPS foi ativada.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPaying(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/regenerate-pix`);
      setInv(data);
      setNeedsRegen(false);
      toast.success("Nova cobrança PIX gerada! O QR Code e o código copia-e-cola foram atualizados.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegenerating(false);
    }
  };

  const validateRenewalCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Digite o código do cupom"); return; }
    setValidatingCoupon(true);
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}`);
      setCouponPreview(data);
      toast.success(`Cupom ${data.code} válido — ${data.discount_pct}% OFF`);
    } catch (e) {
      setCouponPreview(null);
      toast.error(formatApiError(e));
    } finally {
      setValidatingCoupon(false);
    }
  };

  const applyRenewalCoupon = async () => {
    if (!couponPreview) return;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/apply-coupon`, {
        coupon_code: couponPreview.code,
      });
      setInv(data.invoice);
      setNeedsRegen(false);
      setCouponPreview(null);
      setCouponInput("");
      if (data.invoice?.status === "paid") {
        toast.success("Cupom aplicado. Renovação confirmada sem cobrança.");
      } else if (data.pix_generated) {
        toast.success("Cupom aplicado e novo PIX gerado com o valor atualizado.");
      } else {
        toast.success("Cupom aplicado. Gere uma nova cobrança PIX para pagar.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally {
      setApplyingCoupon(false);
    }
  };

  if (!inv) return <div className="text-zinc-500">Carregando…</div>;

  const paid = inv.status === "paid";
  const hasRealMP = !!inv.mp_payment_id;
  const mpCancelled = !isCard && ["cancelled", "expired", "refunded", "charged_back"].includes(inv.mp_status);
  const cancelled = inv.status === "cancelled" || mpCancelled;
  const needsFirstPix = !isCard && !paid && !inv.pix_code && !inv.mp_payment_id;
  const showRegenAlert = !isCard && !paid && (needsRegen || mpCancelled || needsFirstPix);

  return (
    <div className="space-y-8" data-testid="invoice-detail-page">
      <Link to="/dashboard/invoices" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// fatura {inv.number}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">{inv.plan_name}</h1>
      </div>

      {showRegenAlert && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pix-expired-alert">
          <AlertCircle className="h-5 w-5 text-amber-400 shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-mono uppercase tracking-widest text-amber-400">
              {needsFirstPix ? "// PIX não gerado" : "// PIX expirado"}
            </div>
            <div className="text-sm text-white mt-0.5">
              {needsFirstPix
                ? "Não foi possível gerar o PIX inicialmente. Gere uma nova cobrança para continuar o pagamento."
                : "O QR Code / código PIX desta fatura expirou no Mercado Pago. Gere uma nova cobrança para continuar o pagamento."}
            </div>
          </div>
          <Button
            onClick={regenerate}
            disabled={regenerating}
            className="rounded-sm bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="regen-pix-btn-alert"
          >
            {regenerating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : <><RefreshCw className="h-4 w-4 mr-2" />Gerar nova cobrança</>}
          </Button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-white/10 bg-[#121214] p-6 space-y-4">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// detalhes</div>
          <ul className="space-y-2 font-mono text-sm">
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">Número</span><span>{inv.number}</span></li>
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">Plano</span><span>{inv.plan_name}</span></li>
            {inv.coupon_code && inv.original_amount_brl != null && (
              <li className="flex justify-between border-b border-white/10 pb-2">
                <span className="text-zinc-500">Valor antes do cupom</span>
                <span>{fmtBRL(inv.original_amount_brl)}</span>
              </li>
            )}
            {inv.coupon_code && (
              <li className="flex justify-between border-b border-white/10 pb-2">
                <span className="text-zinc-500">Cupom {inv.coupon_code}</span>
                <span className="text-emerald-300">{inv.coupon_discount_pct}% OFF</span>
              </li>
            )}
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">Valor</span><span className="text-white text-lg">{fmtBRL(inv.amount_brl)}</span></li>
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">Vencimento</span><span>{fmtDate(inv.due_at)}</span></li>
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">Método</span><span>{couponCoversInvoice ? "Cupom · sem cobrança" : isCard ? "Checkout Mercado Pago · cartão ou saldo" : `PIX ${hasRealMP ? "· Mercado Pago" : "· Mock"}`}</span></li>
            {hasRealMP && (
              <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-zinc-500">MP ID</span><span className="text-zinc-400 text-xs">{inv.mp_payment_id}</span></li>
            )}
            <li className="flex justify-between"><span className="text-zinc-500">Status</span><span className={paid ? "text-emerald-400" : cancelled ? "text-red-400" : "text-amber-400"}>{paid ? "PAGA" : cancelled ? (isCard ? "CANCELADA" : "PIX EXPIRADO") : `PENDENTE${polling ? " (verificando…)" : ""}`}</span></li>
            {inv.pix_regenerated_at && !paid && (
              <li className="flex justify-between text-xs text-zinc-500 pt-1"><span>PIX atualizado em</span><span>{fmtDate(inv.pix_regenerated_at)}</span></li>
            )}
          </ul>
        </div>

        <div className="border border-white/10 bg-[#121214] p-6 space-y-4" data-testid="invoice-payment-box">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// pagamento {isCard ? "no Mercado Pago" : "pix"}</div>
          {inv.is_renewal && !paid && !isCard && !inv.coupon_code && (
            <div className="space-y-3 border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="renewal-coupon-box">
              <label htmlFor="renewal-coupon-input" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400">
                <Ticket className="h-3.5 w-3.5 text-emerald-400" /> Cupom para renovação
              </label>
              {!couponPreview ? (
                <div className="flex gap-2">
                  <Input
                    id="renewal-coupon-input"
                    value={couponInput}
                    onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
                    onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), validateRenewalCoupon())}
                    placeholder="Digite o código"
                    className="rounded-sm bg-[#09090B] border-white/10 font-mono uppercase"
                    data-testid="renewal-coupon-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validateRenewalCoupon}
                    disabled={validatingCoupon || !couponInput.trim()}
                    className="rounded-sm border-white/20 hover:bg-white hover:text-black"
                    data-testid="renewal-coupon-validate"
                  >
                    {validatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verificar"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <div>
                      <div className="font-mono text-emerald-300">{couponPreview.code} · {couponPreview.discount_pct}% OFF</div>
                      {couponPreview.description && <div className="mt-1 text-xs text-zinc-400">{couponPreview.description}</div>}
                      <div className="mt-2 text-xs text-zinc-400">
                        Novo valor: <span className="font-mono text-white">{fmtBRL(Math.round(Number(inv.amount_brl) * (1 - couponPreview.discount_pct / 100) * 100) / 100)}</span>
                      </div>
                    </div>
                    <button type="button" onClick={() => setCouponPreview(null)} aria-label="Remover cupom" data-testid="renewal-coupon-remove">
                      <X className="h-4 w-4 text-zinc-500 hover:text-white" />
                    </button>
                  </div>
                  <Button
                    type="button"
                    onClick={applyRenewalCoupon}
                    disabled={applyingCoupon}
                    className="w-full rounded-sm bg-emerald-500 text-black hover:bg-emerald-400"
                    data-testid="renewal-coupon-apply"
                  >
                    {applyingCoupon ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Aplicando…</> : "Aplicar cupom"}
                  </Button>
                </div>
              )}
            </div>
          )}
          {inv.coupon_code && (
            <div className="border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-300" data-testid="renewal-coupon-applied">
              Cupom {inv.coupon_code} aplicado — {inv.coupon_discount_pct}% OFF.
            </div>
          )}
          {paid ? (
            <div className="flex flex-col items-center text-center py-8">
              {inv.is_plan_upgrade && inv.upgrade_application_state === "failed"
                ? <AlertCircle className="h-16 w-16 text-amber-400 mb-3" strokeWidth={1.5} />
                : <CheckCircle2 className="h-16 w-16 text-emerald-500 mb-3" strokeWidth={1.5} />}
              <div className="font-heading text-xl font-bold">
                {inv.is_plan_upgrade
                  ? inv.upgrade_application_state === "applied"
                    ? "Upgrade aplicado"
                    : inv.upgrade_application_state === "failed"
                      ? "Upgrade em análise"
                      : "Aplicando upgrade…"
                  : "Fatura paga"}
              </div>
              {inv.is_plan_upgrade && inv.upgrade_application_state === "failed" && (
                <div className="text-xs text-amber-300 mt-2">
                  O pagamento foi confirmado. A equipe precisa revisar a aplicação dos novos recursos.
                </div>
              )}
              {inv.is_plan_upgrade && inv.upgrade_application_state === "applied" && (
                <div className="text-xs text-emerald-300 mt-2">
                  Os novos recursos já estão ativos nesta VPS.
                </div>
              )}
              <div className="text-xs text-zinc-500 font-mono mt-1">{fmtDate(inv.paid_at)}</div>
            </div>
          ) : isCard && inv.status !== "pending" ? (
            <div className="text-sm text-amber-300 border border-amber-500/30 p-4 rounded-sm">
              Este checkout não está mais disponível. Se você já pagou, entre em contato com o suporte e informe o número da fatura.
            </div>
          ) : isCard ? (
             <div className="space-y-4">
               <div className="rounded-sm border border-blue-400/20 bg-blue-400/5 p-4">
                 <div className="font-medium">Pagamento seguro com Mercado Pago</div>
                 <div className="text-xs text-zinc-500 mt-1">Você será direcionado ao Mercado Pago para pagar com cartão ou saldo da sua conta. Nenhum dado do cartão é armazenado neste site.</div>
               </div>
               {inv.mp_checkout_url ? (
                 <a href={inv.mp_checkout_url} target="_blank" rel="noopener noreferrer" className="block" data-testid="mp-card-checkout-link">
                   <Button className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp">
                     <ExternalLink className="h-4 w-4 mr-2" /> Pagar no Mercado Pago
                   </Button>
                 </a>
               ) : (
                 <div className="text-sm text-amber-300 border border-amber-500/30 p-3 rounded-sm">O link de pagamento ainda está sendo preparado. Atualize esta página em instantes.</div>
               )}
               <div className="flex items-center justify-center gap-2 text-xs font-mono text-zinc-500 pt-2">
                 {polling ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Verificando confirmação do pagamento…</> : "Aguardando confirmação do Mercado Pago…"}
               </div>
               <div className="text-xs text-zinc-500 text-center">Ao retornar do Mercado Pago, a confirmação pode levar alguns instantes. O status só muda após validação.</div>
             </div>
           ) : (
            <>
              {inv.pix_qr_url && (
                <div className={`border border-white/10 bg-white p-3 rounded-sm relative ${mpCancelled ? "opacity-40" : ""}`}>
                  <img src={inv.pix_qr_url} alt="PIX QR Code" className="w-full" data-testid="pix-qr-image" />
                  {mpCancelled && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-red-500 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm rotate-[-6deg] shadow-lg">expirado</span>
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Copia e cola</div>
              <div className={`border border-white/10 bg-[#09090B] p-3 font-mono text-[10px] break-all ${mpCancelled ? "opacity-40" : ""}`} data-testid="pix-code-block">{inv.pix_code}</div>
              <Button
                onClick={() => { navigator.clipboard.writeText(inv.pix_code); toast.success("Código copiado"); }}
                variant="outline"
                disabled={mpCancelled}
                className="w-full rounded-sm border-white/20 hover:bg-white hover:text-black disabled:opacity-40"
                data-testid="copy-pix-btn"
              >
                <Copy className="h-4 w-4 mr-2" /> Copiar código PIX
              </Button>
              {inv.mp_ticket_url && !mpCancelled && (
                <a href={inv.mp_ticket_url} target="_blank" rel="noreferrer" className="block" data-testid="mp-ticket-link">
                  <Button variant="outline" className="w-full rounded-sm border-white/20 hover:bg-white hover:text-black">
                    <ExternalLink className="h-4 w-4 mr-2" /> Abrir no Mercado Pago
                  </Button>
                </a>
              )}
              {hasRealMP && (
                <Button
                  onClick={regenerate}
                  disabled={regenerating}
                  variant="outline"
                  className="w-full rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black"
                  data-testid="regen-pix-btn"
                >
                  {regenerating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : <><RefreshCw className="h-4 w-4 mr-2" />Atualizar QR / Gerar nova cobrança</>}
                </Button>
              )}
              {polling && !mpCancelled && (
                <div className="flex items-center justify-center gap-2 text-xs font-mono text-zinc-500 pt-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aguardando confirmação do PIX…
                </div>
              )}
              {!hasRealMP && !inv.payment_error && (
                <Button onClick={simulatePay} disabled={paying} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="simulate-pay-btn">
                  {paying ? "Confirmando…" : "Simular pagamento (mock)"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
